<script>
import { mapGetters } from 'vuex';
import AgentTable from './components/overview/AgentTable.vue';
import AnimatedMetricValue from './components/overview/AnimatedMetricValue.vue';
import MetricCard from './components/overview/MetricCard.vue';
import { OVERVIEW_METRICS } from './constants';
import ReportsFiltersComunicanteCampaign from './components/Filters/ComunicanteCampaign.vue';
import ReportHeatmap from './components/Heatmap.vue';

import endOfDay from 'date-fns/endOfDay';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import { emitter } from 'shared/helpers/mitt';

export default {
  name: 'LiveReports',
  components: {
    AgentTable,
    AnimatedMetricValue,
    MetricCard,
    ReportHeatmap,
    ReportsFiltersComunicanteCampaign,
  },
  data() {
    return {
      pageIndex: 0,
      heatmapOnlyAssigned: false,
      labelOnlyAssignedToggle: 'Somente conversas atribuídas',
      comunicanteCampaign: 'all',
    };
  },
  computed: {
    ...mapGetters({
      agentStatus: 'agents/getAgentStatus',
      agents: 'agents/getAgents',
      accountConversationMetric: 'getAccountConversationMetric',
      agentConversationMetric: 'getAgentConversationMetric',
      agentConversationMetricTotal: 'getAgentConversationMetricTotal',
      accountConversationHeatmap: 'getAccountConversationHeatmapData',
      uiFlags: 'getOverviewUIFlags',
    }),
    agentStatusMetrics() {
      return Object.keys(this.agentStatus).map(key => {
        const metricName = this.$t(
          `OVERVIEW_REPORTS.AGENT_STATUS.${OVERVIEW_METRICS[key]}`
        );

        return {
          key,
          name: metricName,
          value: this.agentStatus[key],
        };
      });
    },
    conversationMetrics() {
      return Object.keys(this.accountConversationMetric).map(key => {
        const metricName = this.$t(
          `OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS.${OVERVIEW_METRICS[key]}`
        );

        return {
          key,
          name: metricName,
          value: this.accountConversationMetric[key],
        };
      });
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.fetchAllData();

    emitter.on('fetch_overview_reports', () => {
      this.fetchAllData();
    });
  },
  methods: {
    fetchAllData() {
      this.fetchAccountConversationMetric();
      this.fetchAgentConversationMetric();
      this.fetchHeatmapData();
    },
    downloadHeatmapData() {
      let to = endOfDay(new Date());

      this.$store.dispatch('downloadAccountConversationHeatmap', {
        to: getUnixTime(to),
        onlyAssigned: this.heatmapOnlyAssigned,
        comunicanteCampaign: this.comunicanteCampaign,
      });
    },
    onlyAssignedToggle() {
      this.fetchHeatmapData(true);
    },
    fetchHeatmapData(all = false) {
      if (this.uiFlags.isFetchingAccountConversationsHeatmap) {
        return;
      }

      // the data for the last 6 days won't ever change,
      // so there's no need to fetch it again
      // but we can write some logic to check if the data is already there
      // if it is there, we can refetch data only for today all over again
      // and reconcile it with the rest of the data
      // this will reduce the load on the server doing number crunching
      let to = endOfDay(new Date());
      let from = startOfDay(subDays(to, 6));

      if (this.accountConversationHeatmap.length && !all) {
        to = endOfDay(new Date());
        from = startOfDay(to);
      }

      this.$store.dispatch('fetchAccountConversationHeatmap', {
        metric: 'conversations_count',
        from: getUnixTime(from),
        to: getUnixTime(to),
        groupBy: 'hour',
        businessHours: false,
        onlyAssigned: this.heatmapOnlyAssigned,
        comunicanteCampaign: this.comunicanteCampaign,
      });
    },
    onComunicanteCampaignChange(selectedCampaign) {
      this.comunicanteCampaign = selectedCampaign.id;
      this.fetchHeatmapData(true);
    },
    fetchAccountConversationMetric() {
      this.$store.dispatch('fetchAccountConversationMetric', {
        type: 'account',
      });
    },
    fetchAgentConversationMetric() {
      this.$store.dispatch('fetchAgentConversationMetric', {
        type: 'agent',
        page: this.pageIndex + 1,
      });
    },
    onPageNumberChange(pageIndex) {
      this.pageIndex = pageIndex;
      this.fetchAgentConversationMetric();
    },
    onFilteredAgent(agentId) {
      this.pageIndex = 0;
      this.$store.dispatch('fetchAgentConversationMetric', {
        type: 'agent',
        agentId,
        page: 1,
      });
    },
    metricItemClass(index) {
      return [
        'flex-1 min-w-0 pb-2',
        index > 0
          ? 'border-t md:border-t-0 md:border-l border-slate-100 dark:border-slate-700 pt-3 md:pt-0 md:pl-6'
          : '',
      ];
    },
    metricValueClass(key) {
      if (key === 'unattended') {
        return 'text-orange-600 dark:text-orange-600';
      }

      if (key === 'resolved_today') {
        return 'text-green-600 dark:text-green-400';
      }

      return 'text-woot-800 dark:text-woot-300';
    },
  },
};
</script>

<template>
  <div class="flex-1 p-4 overflow-auto">
    <div class="flex flex-col items-center md:flex-row">
      <div
        class="flex-1 w-full max-w-full md:w-[65%] md:max-w-[65%] conversation-metric"
      >
        <MetricCard
          :header="$t('OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS.HEADER')"
          :is-loading="
            uiFlags.isFetchingAccountConversationMetric &&
            !conversationMetrics.length
          "
          :loading-message="
            $t('OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS.LOADING_MESSAGE')
          "
        >
          <div
            v-for="(metric, index) in conversationMetrics"
            :key="metric.key"
            :class="metricItemClass(index)"
          >
            <h3 class="text-base text-slate-700 dark:text-slate-100">
              {{ metric.name }}
            </h3>
            <p class="text-3xl mb-0 mt-1" :class="metricValueClass(metric.key)">
              <AnimatedMetricValue
                :value="metric.value"
                :value-class="metricValueClass(metric.key)"
                empty-value="0"
                show-zero
              />
            </p>
          </div>
        </MetricCard>
      </div>
      <div class="flex-1 w-full max-w-full md:w-[35%] md:max-w-[35%]">
        <MetricCard :header="$t('OVERVIEW_REPORTS.AGENT_STATUS.HEADER')">
          <div
            v-for="(metric, index) in agentStatusMetrics"
            :key="metric.key"
            :class="metricItemClass(index)"
          >
            <h3 class="text-base text-slate-700 dark:text-slate-100">
              {{ metric.name }}
            </h3>
            <p class="text-woot-800 dark:text-woot-300 text-3xl mb-0 mt-1">
              <AnimatedMetricValue
                :value="metric.value"
                value-class="text-woot-800 dark:text-woot-300"
                empty-value="0"
                show-zero
              />
            </p>
          </div>
        </MetricCard>
      </div>
    </div>
    <div class="flex flex-row flex-wrap max-w-full ml-auto mr-auto">
      <MetricCard :header="$t('OVERVIEW_REPORTS.CONVERSATION_HEATMAP.HEADER')">
        <template #control>
          <span class="mx-2 text-sm whitespace-nowrap">
            {{ labelOnlyAssignedToggle }}
          </span>
          <span>
            <woot-switch
              v-model="heatmapOnlyAssigned"
              @click="onlyAssignedToggle"
            />
          </span>
          <ReportsFiltersComunicanteCampaign
            class="min-w-[250px]"
            @comunicante-campaign-filter-selection="onComunicanteCampaignChange"
          />
          <woot-button
            icon="arrow-download"
            size="small"
            variant="smooth"
            color-scheme="secondary"
            @click="downloadHeatmapData"
          >
            {{ $t('OVERVIEW_REPORTS.CONVERSATION_HEATMAP.DOWNLOAD_REPORT') }}
          </woot-button>
        </template>
        <ReportHeatmap
          :heat-data="accountConversationHeatmap"
          :is-loading="
            uiFlags.isFetchingAccountConversationsHeatmap &&
            !accountConversationHeatmap.length
          "
        />
      </MetricCard>
    </div>
    <div class="flex flex-row flex-wrap max-w-full ml-auto mr-auto">
      <MetricCard :header="$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.HEADER')">
        <AgentTable
          :agents="agents"
          :agent-metrics="agentConversationMetric"
          :total-count="agentConversationMetricTotal"
          :page-index="pageIndex"
          :is-loading="
            uiFlags.isFetchingAgentConversationMetric &&
            !agentConversationMetric.length
          "
          @page-change="onPageNumberChange"
          @filtered-agent="onFilteredAgent"
        />
      </MetricCard>
    </div>
  </div>
</template>
